import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, BookOpen, Activity, Briefcase, Globe, Users, ArrowRight, Calendar, Mail } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.7,
    ease: 'easeOut'
  }
};
function AnimatedCounter({
  value,
  suffix = ''
}: {
  value: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000; // 2 seconds
      const increment = value / (duration / 16); // 60fps

      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  return <span ref={ref} data-id="element-911" data-mp="2qybxmcdagm5i">
      {count}
      {suffix}
    </span>;
}
export function Home() {
  return <div className="pt-0" data-id="element-912" data-mp="2mxa6kitizgem">
      {/* Premium Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-16 overflow-hidden bg-navy" data-id="element-913" data-mp="2mwqlwuchqdww">
        {/* Premium Background Glows */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold/20 rounded-full blur-[128px] mix-blend-screen pointer-events-none" data-id="element-914" data-mp="2d7gxppkrpfe2" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-burgundy/20 rounded-full blur-[128px] mix-blend-screen pointer-events-none" data-id="element-915" data-mp="2895jz3rcqkqk" />

        <div className="container relative z-10 mx-auto px-6 md:px-12" data-id="element-916" data-mp="2376k43utm5yd">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center" data-id="element-917" data-mp="1z6wamh7tkxer">
            {/* Left Content */}
            <div className="lg:w-1/2" data-id="element-918" data-mp="1uafo1rg9qr3s">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} data-id="element-919" data-mp="1kk7fssoij8ep">
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6" data-id="element-920" data-mp="1an2q4ivgkut5">
                  <span className="w-2 h-2 rounded-full bg-gold animate-pulse" data-id="element-921" data-mp="1flw6qk61l1xj" />
                  <span className="text-sm font-medium text-white/90 tracking-wide uppercase" data-id="element-922" data-mp="23cjglaziq1mq">
                    Poch Global Initiative
                  </span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-[1.1] mb-6" data-id="element-923" data-mp="1tm6uvffyw2f0">
                  Connect with <br className="hidden sm:block" data-id="element-924" data-mp="1yinm4iigqwwb" />
                  <span className="text-transparent bg-clip-text bg-accent-gradient italic pr-4" data-id="element-925" data-mp="2m9klkvt2o1qo">
                    Your Roots.
                  </span>
                  <br data-id="element-926" data-mp="2r2vx3a8tx6ox" />
                  Empower Your Future.
                </h1>

                <p className="text-base md:text-lg text-white/70 mb-8 max-w-xl leading-relaxed font-light" data-id="element-927" data-mp="fk98vhy3atm2">
                  Africa's premier platform connecting people worldwide to
                  heritage, culture, education, and innovation. Join a global
                  community building bridges between Africa and the world.
                </p>

                <div className="flex flex-col sm:flex-row gap-4" data-id="element-928" data-mp="3sru3v8yaykcw">
                  <Link to="/programs" className="w-full sm:w-auto justify-center px-6 py-3 bg-gold hover:bg-gold-light text-white rounded-full font-semibold transition-all duration-300 shadow-[0_0_40px_-10px_rgba(200,146,17,0.5)] hover:-translate-y-1 flex items-center gap-2" data-id="element-929" data-mp="3su6x4i9igh59">
                    Explore Programs <ArrowRight className="w-4 h-4" data-id="element-930" data-mp="1h1rw9yd0eyl6" />
                  </Link>
                  <Link to="/donate" className="w-full sm:w-auto justify-center px-6 py-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 backdrop-blur-md rounded-full font-semibold transition-all duration-300 flex items-center gap-2 hover:-translate-y-1" data-id="element-931" data-mp="1c485jyz4ktcc">
                    <Heart className="w-4 h-4 text-white" data-id="element-932" data-mp="2hb9jgf5qgbpc" /> Donate Now
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Right Visuals - Dynamic Collage */}
            <div className="lg:w-1/2 relative w-full h-[350px] sm:h-[400px] lg:h-[450px] mt-10 lg:mt-0" data-id="element-933" data-mp="2qch968u6o47t">
              {/* Main Image */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9,
              rotate: -2
            }} animate={{
              opacity: 1,
              scale: 1,
              rotate: 0
            }} transition={{
              duration: 1,
              delay: 0.2
            }} className="absolute top-0 right-0 w-4/5 h-[240px] sm:h-[280px] lg:h-[320px] rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl z-10" data-id="element-934" data-mp="302u3gsrsvbr5">
                <img src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2940&auto=format&fit=crop" alt="Community" className="w-full h-full object-cover" data-id="element-935" data-mp="12ezz2b3zh10o" />
                <div className="absolute inset-0 bg-navy/20 mix-blend-multiply" data-id="element-936" data-mp="tf34xtytr9y8" />
              </motion.div>

              {/* Secondary Image */}
              <motion.div initial={{
              opacity: 0,
              y: 50
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: 0.4
            }} className="absolute bottom-0 left-0 w-3/5 h-[160px] sm:h-[200px] lg:h-[240px] rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl z-20" data-id="element-937" data-mp="1m2zc5sm0sk99">
                <img src="https://images.unsplash.com/photo-1528301721190-186c3bd85418?q=80&w=2940&auto=format&fit=crop" alt="Education" className="w-full h-full object-cover" data-id="element-938" data-mp="17djo4kdvisx9" />
              </motion.div>

              {/* Floating Impact Card 1 */}
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-4 lg:top-8 -left-2 lg:-left-6 z-30 bg-white/10 backdrop-blur-xl border border-white/20 p-3 lg:p-4 rounded-2xl shadow-glass" data-id="element-939" data-mp="3ucp0dlqof5pw">
                <div className="flex items-center gap-3" data-id="element-940" data-mp="3aopm40wwlgfq">
                  <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold" data-id="element-941" data-mp="3fmran1d3m7yl">
                    <Users className="w-4 h-4 lg:w-5 lg:h-5" data-id="element-942" data-mp="31nrhphijz6wm" />
                  </div>
                  <div data-id="element-943" data-mp="36ljkj6kqkdfl">
                    <div className="text-lg lg:text-xl font-bold text-white" data-id="element-944" data-mp="36muph9px3r8a">
                      2.5M+
                    </div>
                    <div className="text-[10px] text-white/70 uppercase tracking-wider" data-id="element-945" data-mp="14u6j4rhz8noa">
                      Lives Impacted
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Impact Card 2 */}
              <motion.div animate={{
              y: [0, 10, 0]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} className="absolute bottom-4 lg:bottom-8 -right-2 lg:-right-4 z-30 bg-white/10 backdrop-blur-xl border border-white/20 p-3 lg:p-4 rounded-2xl shadow-glass" data-id="element-946" data-mp="19wnrpzwlinpv">
                <div className="flex items-center gap-3" data-id="element-947" data-mp="26p152ux8act7">
                  <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-burgundy/20 flex items-center justify-center text-burgundy-light" data-id="element-948" data-mp="26mezv1xt89e5">
                    <Globe className="w-4 h-4 lg:w-5 lg:h-5 text-white" data-id="element-949" data-mp="c9ylteyrvqyq" />
                  </div>
                  <div data-id="element-950" data-mp="89wtvxiuabwn">
                    <div className="text-lg lg:text-xl font-bold text-white" data-id="element-951" data-mp="8apqi5o33l12">
                      42
                    </div>
                    <div className="text-[10px] text-white/70 uppercase tracking-wider" data-id="element-952" data-mp="ws8ta4mwgafx">
                      Countries
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Program Overview */}
      <section className="py-12 lg:py-16 bg-warmwhite" data-id="element-953" data-mp="dv7c7lg2mjvm">
        <div className="container mx-auto px-6 md:px-12" data-id="element-954" data-mp="dws6d9yh40sy">
          <div className="flex justify-between items-end mb-16" data-id="element-955" data-mp="itsctlz8j5kl">
            <div data-id="element-956" data-mp="iw53cnj26o99">
              <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-957" data-mp="mw5fu2mlkpdo">
                Our Programs
              </h2>
              <p className="text-mediumgray text-lg max-w-2xl" data-id="element-958" data-mp="rv8khlyo0tl4">
                Comprehensive initiatives designed to empower, educate, and
                inspire communities across Africa and the world.
              </p>
            </div>
            <Link to="/programs" className="hidden md:flex items-center gap-2 text-gold font-semibold hover:text-gold-light transition-colors" data-id="element-959" data-mp="238atnvyfqgv9">
              View All Programs <ArrowRight className="w-4 h-4" data-id="element-960" data-mp="28h2e8y6xh38k" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-961" data-mp="23mpuen1jhpwa">
            {[{
            title: 'Education & Academics',
            icon: BookOpen,
            desc: 'Empowering the next generation through academic enrichment, coding, and cultural arts.'
          }, {
            title: 'Health & Wellness',
            icon: Activity,
            desc: 'Promoting health awareness and behavioral wellness for thriving communities.'
          }, {
            title: 'Business & Entrepreneurship',
            icon: Briefcase,
            desc: 'Transforming ideas into impact with skills training and mentorship.'
          }, {
            title: 'Cultural Heritage',
            icon: Globe,
            desc: 'Experience authentic African culture through immersive tours and events.'
          }, {
            title: 'Community Outreach',
            icon: Users,
            desc: 'Supporting communities through direct outreach, scholarships, and donations.'
          }].map((prog, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white p-8 rounded-3xl shadow-sm border border-softgray hover:shadow-premium hover:border-gold/30 transition-all duration-300 group" data-id="element-962" data-mp="3q4123a81o779" data-mpi={i}>
                <div className="w-14 h-14 bg-gold/10 text-gold rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-963" data-mp="37d05gsuecnqv">
                  <prog.icon className="w-7 h-7" data-id="element-964" data-mp="32dds8jekj7i2" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-965" data-mp="1hvmfba9d2hek">
                  {prog.title}
                </h3>
                <p className="text-mediumgray mb-6" data-id="element-966" data-mp="1mncw9z2t03ey">{prog.desc}</p>
                <Link to="/programs" className="text-gold font-semibold flex items-center gap-2 group-hover:gap-3 transition-all" data-id="element-967" data-mp="18vpyfut7qmu2">
                  Learn More <ArrowRight className="w-4 h-4" data-id="element-968" data-mp="20mm162lk7b3m" />
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Featured Events */}
      <section className="py-16 lg:py-20 bg-white border-y border-softgray" data-id="element-969" data-mp="1rlo4k5dga5v2">
        <div className="container mx-auto px-6 md:px-12" data-id="element-970" data-mp="1rns5qfjysqrn">
          <div className="flex justify-between items-end mb-16" data-id="element-971" data-mp="1wjpostmm4b84">
            <div data-id="element-972" data-mp="g6sgxtwtsu2d">
              <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-973" data-mp="g698r4wz9k1j">
                Upcoming Events
              </h2>
              <p className="text-mediumgray text-lg max-w-2xl" data-id="element-974" data-mp="l76q5fa5ya1z">
                Join our community events designed to educate, inspire, and connect.
              </p>
            </div>
            <Link to="/events" className="hidden md:flex items-center gap-2 text-gold font-semibold hover:text-gold-light transition-colors" data-id="element-975" data-mp="2hwpxz3j1sffp">
              View All Events <ArrowRight className="w-4 h-4" data-id="element-976" data-mp="292blf8pwjj0a" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-977" data-mp="244ta5boewrp4">
            {[{
            title: 'African Cultural Festival',
            date: 'Aug 15, 2026',
            time: '10:00 AM',
            img: 'https://images.unsplash.com/photo-1533222481259-ce20eda1e20b?q=80&w=2940&auto=format&fit=crop',
            desc: 'A full day celebrating African heritage, music, art, and cuisine.'
          }, {
            title: 'Entrepreneurship Seminar',
            date: 'Jul 21, 2026',
            time: '2:00 PM',
            img: 'https://images.unsplash.com/photo-1515162816999-a0c47dc192f7?q=80&w=2940&auto=format&fit=crop',
            desc: 'Learn from successful business leaders and network with peers.'
          }, {
            title: 'Youth Innovation Workshop',
            date: 'Weekly',
            time: '4:00 PM',
            img: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2940&auto=format&fit=crop',
            desc: 'Hands-on coding and robotics training for the next generation.'
          }].map((event, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-warmwhite rounded-3xl overflow-hidden shadow-sm border border-softgray hover:shadow-premium transition-all duration-300 group flex flex-col" data-id="element-978" data-mp="wcbeilt5t4i2" data-mpi={i}>
                <div className="h-48 overflow-hidden relative" data-id="element-979" data-mp="2csxivnjlmtjb">
                  <img src={event.img} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-980" data-mp="2baqgb4ln6i21" />
                </div>
                <div className="p-6 flex flex-col flex-grow" data-id="element-981" data-mp="2g8rzdpj2ihdi">
                  <h3 className="text-xl font-bold text-navy mb-3" data-id="element-982" data-mp="2g776038hp4h8">
                    {event.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-mediumgray mb-4" data-id="element-983" data-mp="2u66t36gctbba">
                    <span className="flex items-center gap-1" data-id="element-984" data-mp="2p7cd0c9ru0uv"><Calendar className="w-4 h-4 text-gold" data-id="element-985" data-mp="33yup3yzifb8y" /> {event.date}</span>
                  </div>
                  <p className="text-mediumgray text-sm mb-6 flex-grow" data-id="element-986" data-mp="2yyq9scac8mkb">
                    {event.desc}
                  </p>
                  <Link to="/events" className="w-full py-3 bg-navy hover:bg-navy-light text-white rounded-xl font-semibold transition-colors text-sm flex items-center justify-center" data-id="element-987" data-mp="3czsrydy7pcpy">
                    Register
                  </Link>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Wonder Boy Feature */}
      <section className="py-16 lg:py-20 bg-navy text-white overflow-hidden" data-id="element-988" data-mp="3ine6f69ql0f7">
        <div className="container mx-auto px-6 md:px-12" data-id="element-989" data-mp="3mktvh1dq4o5e">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-990" data-mp="38uyog8yfwbub">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-1/2" data-id="element-991" data-mp="3dlow5suc8qs6">
              <span className="text-gold-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-992" data-mp="2yv7uq9hpe0va">
                Featured Initiative
              </span>
              <h2 className="text-4xl text-white md:text-5xl font-serif font-bold mb-6" data-id="element-993" data-mp="2pxp6yxcod8fp">
                Experience Wonder Boy
              </h2>
              <p className="text-lg text-white/80 mb-8 leading-relaxed" data-id="element-994" data-mp="2uxa91gn55wwn">
                Our flagship restaurant and training center where theory meets
                practice. Experience authentic African dining while observing
                hands-on culinary and hospitality training that empowers local
                youth with lifelong career skills.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-995" data-mp="mporgd7rqojz">
                <Link to="/about" className="w-full sm:w-auto text-center px-8 py-4 bg-gold hover:bg-gold-light text-white rounded-full font-semibold transition-all shadow-lg hover:-translate-y-1" data-id="element-996" data-mp="1jd1wqk829tq5">
                  Visit Wonder Boy
                </Link>
                <button className="w-full sm:w-auto text-center px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full font-semibold transition-all" data-id="element-997" data-mp="15dk2vqfwrq9x">
                  Book Training
                </button>
              </div>
            </motion.div>
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-1/2 relative" data-id="element-998" data-mp="25n1ufbiya9dk">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10" data-id="element-999" data-mp="2engiginau2kr">
                <img src="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?q=80&w=2940&auto=format&fit=crop" alt="Wonder Boy Restaurant" className="w-full h-[500px] object-cover" data-id="element-1000" data-mp="2jdomwly4b317" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" data-id="element-1001" data-mp="2te7btv4jh9pz" />
                <div className="absolute bottom-8 left-8 right-8" data-id="element-1002" data-mp="2ybz5ouoqv5wt">
                  <div className="bg-white/90 backdrop-blur-md border border-white/20 p-6 rounded-2xl" data-id="element-1003" data-mp="32brf19lypg38">
                    <h4 className="text-xl font-bold text-navy mb-2" data-id="element-1004" data-mp="13yhmtkzaax84">
                      Authentic Cuisine & Training
                    </h4>
                    <p className="text-mediumgray text-sm" data-id="element-1005" data-mp="p2j7y93cdplz">
                      Empowering 50+ students annually through hands-on culinary
                      arts.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 lg:py-16 bg-white text-white relative overflow-hidden" data-id="element-1006" data-mp="tuj2tn4k1sbp">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-1007" data-mp="64or58vz29t1" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-1008" data-mp="1655zap7d21q">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-1009" data-mp="f5ei9zaafc0f">
            {[{
            value: 8,
            suffix: '+',
            label: 'Years of Service'
          }, {
            value: 50,
            suffix: '+',
            label: 'Programs Delivered'
          }, {
            value: 10,
            suffix: 'k+',
            label: 'Youth Empowered'
          }, {
            value: 100,
            suffix: '+',
            label: 'Communities Reached'
          }].map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} data-id="element-1010" data-mp="1p2bl9y3xq7tw" data-mpi={i}>
                <div className="text-5xl md:text-6xl font-serif font-bold mb-2 text-navy" data-id="element-1011" data-mp="2805r639ro382">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} data-id="element-1012" data-mp="2vpqltjfa8ox6" />
                </div>
                <div className="text-mediumgray font-medium uppercase tracking-wider text-sm" data-id="element-1013" data-mp="30hrxb34mmbm5">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Latest Stories */}
      <section className="py-12 lg:py-16 bg-white border-b border-softgray" data-id="element-1014" data-mp="m7exk85wu1vz">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1015" data-mp="m5avlvrkncyc">
          <div className="flex justify-between items-end mb-16" data-id="element-1016" data-mp="r5gfgprlnv5v">
            <div data-id="element-1017" data-mp="c97jpy6x3zy7">
              <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-1018" data-mp="c8ppf73tfvsx">
                Latest Stories
              </h2>
              <p className="text-mediumgray text-lg max-w-2xl" data-id="element-1019" data-mp="h93lrrw21hb4">
                Insights, news, and inspiring stories from our community.
              </p>
            </div>
            <Link to="/blog" className="hidden md:flex items-center gap-2 text-gold font-semibold hover:text-gold-light transition-colors" data-id="element-1020" data-mp="874899k90vcx">
              View All Posts <ArrowRight className="w-4 h-4" data-id="element-1021" data-mp="22m6qcm6yxb5t" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1022" data-mp="2cplk8g1w8ld4">
            {[{
            title: 'Empowering Youth Through Tech',
            category: 'Education',
            img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2942&auto=format&fit=crop',
            excerpt: 'How our new coding bootcamp is changing lives and creating future leaders in tech.'
          }, {
            title: 'The Importance of Cultural Preservation',
            category: 'Culture',
            img: 'https://images.unsplash.com/photo-1531206715517-5c0ba140b2b8?q=80&w=2940&auto=format&fit=crop',
            excerpt: 'Why connecting with our roots is more important than ever in a globalized world.'
          }, {
            title: 'Success Story: Wonder Boy Graduates',
            category: 'Success Stories',
            img: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?q=80&w=2877&auto=format&fit=crop',
            excerpt: 'Meet the latest cohort of culinary students who have successfully launched their careers.'
          }].map((post, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="group cursor-pointer" data-id="element-1023" data-mp="3f3jo2ii3m8ml" data-mpi={i}>
                <div className="h-64 rounded-3xl overflow-hidden mb-6 relative shadow-sm" data-id="element-1024" data-mp="3o3zo1hzmuxmp">
                  <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1025" data-mp="3t2sr0ghan68m" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-navy uppercase tracking-wider" data-id="element-1026" data-mp="6nhqs0oix7p2">
                    {post.category}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-navy mb-3 group-hover:text-gold transition-colors" data-id="element-1027" data-mp="206qeccodd79v">
                  {post.title}
                </h3>
                <p className="text-mediumgray mb-4 line-clamp-2" data-id="element-1028" data-mp="1w43nof3fpse9">
                  {post.excerpt}
                </p>
                <span className="text-gold font-semibold flex items-center gap-2 text-sm" data-id="element-1029" data-mp="1r84zari3wadu">
                  Read More <ArrowRight className="w-4 h-4" data-id="element-1030" data-mp="2juzk5piu5bru" />
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 lg:py-20 bg-lightgray" data-id="element-1031" data-mp="2ey8v4cp1z5ua">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1032" data-mp="2a3v4sop8zowh">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1033" data-mp="256cscoc0urjq">
            <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-1034" data-mp="253z1krodk1hz">
              Be Part of Something Bigger
            </h2>
            <p className="text-mediumgray text-lg" data-id="element-1035" data-mp="2xv0j2zfji94o">
              There are many ways to get involved and support our mission to
              connect Africa with the world.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1036" data-mp="2meu9e8reoom1">
            {[{
            title: 'Donate',
            desc: 'Support our mission',
            icon: Heart,
            link: '/donate',
            btn: 'Donate'
          }, {
            title: 'Volunteer',
            desc: 'Give your time',
            icon: Users,
            link: '/volunteer',
            btn: 'Volunteer'
          }, {
            title: 'Partner',
            desc: 'Collaborate with us',
            icon: Briefcase,
            link: '/contact',
            btn: 'Partner'
          }, {
            title: 'Programs',
            desc: 'Join a program',
            icon: BookOpen,
            link: '/programs',
            btn: 'Apply'
          }].map((action, i) => <div key={i} className="bg-white p-8 rounded-3xl text-center border border-softgray shadow-sm hover:shadow-premium transition-all duration-300" data-id="element-1037" data-mp="1dbpv1n24wclc" data-mpi={i}>
                <div className="w-16 h-16 mx-auto bg-navy/5 text-navy rounded-full flex items-center justify-center mb-6" data-id="element-1038" data-mp="1m7yxlalerrbp">
                  <action.icon className="w-8 h-8" data-id="element-1039" data-mp="ueot6b3vmslm" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1040" data-mp="ugt3pbwa6puv">
                  {action.title}
                </h3>
                <p className="text-mediumgray mb-8" data-id="element-1041" data-mp="13fekb1i6zut0">{action.desc}</p>
                <Link to={action.link} className="inline-block w-full py-3 bg-warmwhite hover:bg-navy hover:text-white text-navy font-semibold rounded-xl transition-colors" data-id="element-1042" data-mp="13djxtir9vumt">
                  {action.btn}
                </Link>
              </div>)}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 lg:py-20 bg-navy text-white relative overflow-hidden" data-id="element-1043" data-mp="17hsrt9qjb23e">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-1044" data-mp="1cg2ip8587gj7" />
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-3xl" data-id="element-1045" data-mp="1ggw865ybh3e7">
          <Mail className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1046" data-mp="1lhaftmw0abbp" />
          <h2 className="text-4xl text-white font-serif font-bold mb-4" data-id="element-1047" data-mp="osugx070aplx">
            Stay Connected with Poch Global
          </h2>
          <p className="text-white/70 text-lg mb-10" data-id="element-1048" data-mp="sto2vo0w1uc6">
            Get updates on upcoming events, new programs, and inspiring stories
            from our community.
          </p>

          <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" data-id="element-1049" data-mp="12jzgy3eadjws">
            <input type="email" placeholder="Enter your email address" className="flex-grow px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all" data-id="element-1050" data-mp="54llmha623i7" />
            <button type="submit" className="px-8 py-4 bg-gold hover:bg-gold-light text-white font-bold rounded-full transition-all shadow-lg hover:shadow-gold/20" data-id="element-1051" data-mp="3tq8987ew3334">
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>;
}